'use strict';

const _ = require('lodash');
const { mapGridView } = require('../data/utils/time-flexible-utils');

const formatProduct = (product, useAbsoluteCompetitorIndex, additionalContentEnhancer) => {
  /**
   * Format a product's information before sending it to the client side.
   * @param product - the product to format
   * @param useAbsoluteCompetitorIndex - flag to determine if competitor index should use absolute or percentage value
   * @param additionalContentEnhancer - function to apply to the product (e.g. addIndexToProductInfo)
   *  this may be an identity function, i.e. the product is returned unchanged.
   * @returns the formatted product
   */
  return additionalContentEnhancer(product, useAbsoluteCompetitorIndex);
};

const calculateCompetitorIndex = (
  competitor,
  clientPriceHistory,
  clientLivePrice,
  useAbsoluteCompetitorIndex
) => {
  /**
   * Calculate the competitor PAS for each week of historical competitor prices.
   * @param competitor - competitor field from the workpackage-product document
   * @param clientPriceHistory - the array of client history (priceHistoryWeek)
   * @param clientLivePrice - thelivePrice object
   * @param useAbsoluteCompetitorIndex - controls whether we should calculate competitor index as an absolute or percentage diff value
   *  When true, measures our price relative to competitors as a fixed percentage. Values below 100 mean own price is more expensive, above 100 mean own price is cheaper.
   *  e.g. our price = 2.80 and competitor price = 2.50, index = 89.29. This means the current price is 10.71% more expensive than the competitor price.
   *  e.g. our price = 2.50 and competitor price = 2.80, index = 112. This means the current price is 12% cheaper than the competitor price.
   *  When false, measures the difference between our price and competitor's price as a percentage ratio. Negative values mean competitor is cheaper, positive mean competitor is more expensive.
   *  e.g. our price = 2.50 and competitor price = 2.80, index = 0.12. This means the competitor price is 12% more expensive than the current price.
   *  e.g. our price = 2.80 and competitor price = 2.50, index = -0.1071. This means the competitor price is 10.71% cheaper than the current price.
   * @returns {Array} - competitor field with competitorIndex field added to competitorHistoryWeek
   */
  const fullClientHistory = [...clientPriceHistory, clientLivePrice];
  const competitorWithIndex = _.map(competitor, c => {
    const historyWithIndex = _.map(c.competitorHistoryWeek, h => {
      const clientPrice = _.get(
        _.find(
          fullClientHistory,
          clientHistory =>
            h.effectiveTimestamp >= clientHistory.effectiveTimestamp &&
            h.effectiveTimestamp < clientHistory.expiryTimestamp
        ),
        'price',
        null
      );
      if (useAbsoluteCompetitorIndex) {
        h.competitorIndex = _.isNil(clientPrice) ? null : (h.price / clientPrice) * 100;
      } else {
        h.competitorIndex = _.isNil(clientPrice) ? null : (h.price - clientPrice) / clientPrice;
      }

      return h;
    });
    c.competitorHistoryWeek = historyWithIndex;

    if (useAbsoluteCompetitorIndex) {
      c.livePriceIndex = (c.competitorPrice / clientLivePrice.price) * 100;
    } else {
      c.livePriceIndex = (c.competitorPrice - clientLivePrice.price) / clientLivePrice.price;
    }

    return c;
  });
  return competitorWithIndex;
};

const addIndexToProductInfo = (product, useAbsoluteCompetitorIndex) => {
  /**
   * Given an individual product, add a competitorIndex field to each element of each competitor's
   * competitorHistoryWeek and return the product
   */
  product.competitor = calculateCompetitorIndex(
    product.competitor,
    product.priceHistoryWeek,
    product.livePrice,
    useAbsoluteCompetitorIndex
  );
  return product;
};

const formatFieldsForExport = product => {
  return {
    productKey: product.productKey,
    productKeyDisplay: product.productKeyDisplay,
    productDescription: product.productDescription,
    productSizeType: product.productSizeType,
    yearlySales: product.yearlySales,
    yearlyVolume: product.yearlyVolume,
    promo: product.mandatoryEngineInputs.promoParticipationSales,
    nonPromoNetCost: product.mandatoryEngineInputs.nonPromoNetCost,
    costDelta: product.costDelta,
    livePrice: product.livePrice,
    intentionPrice: product.intentionPrice,
    intentionMargin: product.intentionMargin,
    scenarioCostImpact: product.scenarioCostImpact,
    scenarioSalesImpact: product.scenarioSalesImpact,
  };
};

const addNormWeightToExport = (currentFields, product) => {
  return {
    ...currentFields,
    normWeight: product.normWeight,
    scenarioPricePerNormWeight: product.scenarioPricePerNormWeight,
    scenarioPricePerContentUnit: product.scenarioPricePerContentUnit,
  };
};

const addStoreGroupToExport = (currentFields, product) => {
  return {
    ...currentFields,
    toolStoreGroupDescription: product.toolStoreGroupDescription,
    storeGroupReferencePrice: product.storeGroupReferencePrice,
  };
};

const addMinMaxReferencePriceToExport = (currentFields, product) => {
  return {
    ...currentFields,
    minMaxReferencePrice: product.minMaxReferencePrice,
  };
};

const addForecastSalesAndVolumeToExport = (currentFields, product) => {
  return {
    ...currentFields,
    forecastVolume: product.forecastVolume,
    forecastSales: product.forecastSales,
  };
};

const useTimeFlexibleDataForExport = (currentFields, product, selectedHistoricalPeriod) => {
  const timeFlexibleFields = mapGridView(selectedHistoricalPeriod, true);
  return { ...currentFields, ...timeFlexibleFields(product) };
};

const useLiveMarginForExport = (currentFields, product) => {
  return {
    ..._.omit(currentFields, ['intentionMargin']),
    liveMargin: product.liveMargin,
  };
};

module.exports = {
  addIndexToProductInfo,
  formatProduct,
  formatFieldsForExport,
  addNormWeightToExport,
  addStoreGroupToExport,
  addMinMaxReferencePriceToExport,
  addForecastSalesAndVolumeToExport,
  useTimeFlexibleDataForExport,
  useLiveMarginForExport,
};
